"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";
import { Menu, X, LogOut, User, ChevronDown, Mail, Clock, Calendar } from "lucide-react";
import { useData } from "../context/DataContext";
import { useLanguage } from "../context/LanguageContext";
import { TopBar, LanguageSelector } from "./TopBar";
import Image from "next/image";

const Header = () => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const { currentUser, logout } = useData();
  const { language, setLanguage, t } = useLanguage();

  const router = useRouter();
  const pathname = usePathname();
  const profileRef = useRef(null);

  const handleLogout = async () => {
    logout();
    setShowMobileMenu(false);
    router.push("/");
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserInitials = () => {
    if (!currentUser) return "U";
    const name = currentUser.full_name || currentUser.name || "User";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const navLinks = [
    { href: "/", label: t("Home") },
    { href: "/about", label: t("About Us") },
    { href: "/services", label: t("Our eVisas") },
    { href: "/faqs", label: t("FAQs") },
    { href: "/track-application", label: t("Track Application") },
    { href: "/contact", label: t("Contact Us") },
  ];

  return (
    <>
      {/* ── Top utility bar ── */}

      <TopBar
        language={language}
        setLanguage={setLanguage}
        currentUser={currentUser}
        getUserInitials={getUserInitials}
        handleLogout={handleLogout}
        router={router}
        t={t}
      />


      {/* ── Main header ── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex justify-between items-stretch h-16">

          {/* Logo */}
          <div
            className="flex items-center gap-2.5 cursor-pointer"
            onClick={() => router.push("/")}
          >
            <Image
              src="/DU-Global-logo.svg"
              alt="DU Digital"
              width={140}
              height={36}
              className="hover:opacity-90 transition"
            />
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-stretch">
            {navLinks.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`
                    flex items-center gap-1 px-4 h-full
                    text-[13px] uppercase tracking-wider transition-colors duration-150
                    ${isActive ? "text-red-800" : "text-gray-900 hover:text-orange-700"}
                  `}
                >
                  <span className="relative pb-1">
                    {item.label}
                    {isActive && (
                      <span className="absolute left-0 right-0 -bottom-1 h-0.5 bg-red-600 rounded-t" />
                    )}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Right controls */}
          {/* <div className="hidden md:flex items-center gap-3">

            <LanguageSelector value={language} onChange={setLanguage} variant="light" />

            {!currentUser ? (
              <>
                <Link
                  href="/login"
                  className="text-sm font-medium text-gray-600 hover:text-orange-600 transition px-1"
                >
                  {t("Login")}
                </Link>

                <Link
                  href="/register"
                  className="flex items-center gap-2 bg-[#1a3a5c] text-white
                             px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider
                             hover:bg-orange-600 transition shadow-sm"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  {t("Register")}
                </Link>
              </>
            ) : (
              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setShowProfileDropdown((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white
                             border border-gray-300 rounded-lg hover:border-orange-500
                             hover:bg-gray-50 transition shadow-sm"
                >
                  <span className="text-gray-700 truncate text-xs font-semibold">
                    {t("Hi")}, {getUserInitials()}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200
                                  rounded-xl shadow-xl py-2 animate-fadeIn z-50">
                    <p className="px-4 py-1 text-gray-700 truncate text-xs font-semibold">
                      {t("Hi")}, {currentUser.full_name}
                    </p>
                    <button
                      onClick={() => router.push("/dashboard/settings")}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <User className="h-4 w-4" />
                      {t("My Profile")}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      <LogOut className="h-4 w-4" />
                      {t("Logout")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div> */}

          {/* Mobile Toggle Button */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden flex items-center justify-center"
          >
            {showMobileMenu ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-gray-100 py-4 space-y-1 animate-slideDown bg-white">
            {navLinks.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setShowMobileMenu(false)}
                className={`block px-6 py-2.5 text-sm font-semibold uppercase tracking-wide transition
                  ${pathname === item.href
                    ? "text-orange-600 border-l-2 border-orange-600 bg-orange-50"
                    : "text-gray-700 hover:text-orange-600 hover:bg-gray-50"
                  }`}
              >
                {item.label}
              </Link>
            ))}

            {/* Language */}
            {/* <div className="px-6 pt-2">
              <select
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  setShowMobileMenu(false);
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white text-sm outline-none"
              >
                <option value="en">English</option>
                <option value="ko">한국어</option>
              </select>
            </div> */}

            {currentUser ? (
              <div className="px-6 pt-2 space-y-1">
                <Link
                  href="/dashboard"
                  onClick={() => setShowMobileMenu(false)}
                  className="block py-2 text-sm text-gray-700 hover:text-orange-600"
                >
                  {t("Dashboard")}
                </Link>
                <Link
                  href="/dashboard/settings"
                  onClick={() => setShowMobileMenu(false)}
                  className="block py-2 text-sm text-gray-700 hover:text-orange-600"
                >
                  {t("My Profile")}
                </Link>
                <button
                  onClick={handleLogout}
                  className="block w-full text-left py-2 text-sm text-red-600"
                >
                  {t("Logout")}
                </button>
              </div>
            ) : (
              <div className="px-6 pt-2 space-y-2">
                <Link
                  href="/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="block py-2 text-sm text-gray-700 hover:text-orange-600"
                >
                  {t("Login")}
                </Link>
                {/* <Link
                  href="/register"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center justify-center gap-2 bg-[#1a3a5c] text-white
                             py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider"
                >
                  <Calendar className="w-4 h-4" />
                  {t("Register")}
                </Link> */}
              </div>
            )}
          </div>
        )}
      </header>
    </>
  );
};

export default Header;